import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useForm } from "../FormContext";
import type { SearchableSelectProps } from "../../../interfaces/components/form";

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  placeholder = "Select an option",
  required,
  disabled,
  insideModal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setFieldValue, setFieldTouched, validateField, fields } = useForm();

  // Get the current field value from form context
  const fieldValue = fields[name]?.value || value || "";
  const fieldErrors = fields[name]?.errors || [];
  const hasError = fieldErrors.length > 0;

  const selectedOption = options.find((option) => option.value === fieldValue);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Calculate dropdown position with enhanced modal support
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = Math.min(300, filteredOptions.length * 36 + 60); // Approximate height

    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top = buttonRect.bottom;
    let left = buttonRect.left;
    const width = buttonRect.width;

    // Positioning logic with improved edge case handling
    // Only position above if there's really not enough space below AND more space above
    if (
      spaceBelow < dropdownHeight &&
      spaceAbove > dropdownHeight &&
      spaceAbove > spaceBelow
    ) {
      // Position above
      top = buttonRect.top - dropdownHeight;
    } else {
      // Default - position below with available space
      top = buttonRect.bottom;

      // If we're in a modal and at the edge, ensure it's visible even if it means overflow
      if (insideModal && spaceBelow < dropdownHeight) {
        // Ensure at least 100px of the dropdown is visible if possible
        const minVisibleHeight = Math.min(100, dropdownHeight);
        if (spaceBelow < minVisibleHeight) {
          // If we can't show minimum height below, try above
          if (spaceAbove > minVisibleHeight) {
            top = buttonRect.top - dropdownHeight;
          }
        }
      }
    }

    // If inside a modal, ensure the dropdown stays within viewport boundaries
    if (insideModal) {
      // Ensure dropdown doesn't go off screen to the right
      if (left + width > viewportWidth - 10) {
        left = viewportWidth - width - 10;
      }

      // Ensure dropdown doesn't go off screen to the left
      if (left < 10) {
        left = 10;
      }

      // Make sure dropdown is visible at the bottom
      if (top + dropdownHeight > viewportHeight - 10) {
        top = viewportHeight - dropdownHeight - 10;
      }

      // Make sure dropdown is visible at the top
      if (top < 10) {
        top = 10;
      }
    }

    setDropdownPosition({
      top,
      left,
      width,
    });
  }, [filteredOptions.length, insideModal]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      // Immediately recalculate position when opening
      calculateDropdownPosition();

      const handleResize = () => calculateDropdownPosition();

      // Use requestAnimationFrame to ensure smooth updates during scrolling
      const handleScrollRAF = () => {
        requestAnimationFrame(calculateDropdownPosition);
      };

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScrollRAF, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScrollRAF, true);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  // Memoize the close dropdown handler
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setFieldTouched(name, true);
    validateField(name);
    onBlur?.();
  }, [name, onBlur, setFieldTouched, validateField]);

  // Handle clicks outside using a capture phase listener
  useEffect(() => {
    if (!isOpen) return;

    function handleGlobalClick(e: MouseEvent) {
      // Get references to current DOM elements
      const selectEl = wrapperRef.current;
      const dropdownEl = dropdownRef.current;
      const targetEl = e.target as Node;

      // Check if click is outside both elements
      const isOutsideSelect = selectEl && !selectEl.contains(targetEl);
      const isOutsideDropdown = dropdownEl && !dropdownEl.contains(targetEl);

      if (isOutsideSelect && isOutsideDropdown) {
        // Close the dropdown asynchronously to avoid React state update conflicts
        setTimeout(() => closeDropdown(), 0);
      }
    }

    // Use capture phase to get event before it reaches other handlers
    document.addEventListener("click", handleGlobalClick, true);

    return () => {
      document.removeEventListener("click", handleGlobalClick, true);
    };
  }, [isOpen, closeDropdown]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDropdown();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, closeDropdown]);

  const handleChange = (newValue: string | number) => {
    setFieldValue(name, newValue);
    onChange?.(newValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Toggle dropdown with position calculation
  const toggleDropdown = () => {
    if (!disabled) {
      // If opening the dropdown, calculate position first
      if (!isOpen) {
        calculateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      <div className="relative" ref={wrapperRef}>
        {label && (
          <label
            htmlFor={name}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div
          className={`relative w-full border rounded-md ${
            hasError ? "border-red-500" : "border-gray-300 dark:border-gray-700"
          } ${disabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
        >
          <button
            ref={buttonRef}
            type="button"
            onClick={toggleDropdown}
            className={`w-full px-2 py-1.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-darkCardBackground dark:text-white ${
              disabled ? "cursor-not-allowed" : ""
            }`}
            disabled={disabled}
          >
            <div className="flex items-center justify-between">
              <span
                className={`block truncate ${!selectedOption ? "text-gray-500" : ""}`}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Dropdown rendered with Portal to avoid modal context issues */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[99999] bg-white dark:bg-darkCardBackground border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-darkCardBackground dark:text-white"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="max-h-60 overflow-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      option.value === fieldValue
                        ? "!bg-[#2823F5] text-white"
                        : ""
                    }`}
                    onClick={() => handleChange(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-2 py-1.5 text-sm text-gray-500">
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
